import { uniqueId } from 'lodash'
import { CreationToolSourceFile } from '../types'
import CreationToolError from '../types/error'
import CreationToolFileType from '../types/file-type'
import getSourceInfo from './get-source-info'

const getImageDuration = (url: string): Promise<number> =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url, true)
        xhr.responseType = 'arraybuffer'
        xhr.onload = () => {
            const buffer = new Uint8Array(xhr.response)
            let duration = 0
            for (let i = 0; i < buffer.length; i++) {
                // find a Graphic Control Extension hex(21F904__ ____ __00)
                if (buffer[i] === 0x21 && buffer[i + 1] === 0xf9 && buffer[i + 2] === 0x04 && buffer[i + 7] === 0x00) {
                    // swap 5th and 6th bytes to get the delay per frame
                    const delay = (buffer[i + 5] << 8) | (buffer[i + 4] & 0xff)
                    duration += delay < 2 ? 10 : delay
                }
            }
            resolve(duration)
        }
        xhr.onerror = reject
        xhr.send()
    })

const checkIfSticker = (img: HTMLImageElement): boolean => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const aspectRatio = img.naturalWidth / img.naturalHeight

    canvas.width = 300
    canvas.height = canvas.width / aspectRatio
    context?.drawImage(img, 0, 0, canvas.width, canvas.height)

    const imageData = context?.getImageData(0, 0, canvas.width, canvas.height)
    if (imageData && imageData.data) {
        const data = imageData.data
        let transparentPixels = 0
        for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3]
            if (alpha === 0) {
                transparentPixels++
            }
        }
        return transparentPixels > 0
    }

    return false
}

const validateImage = (
    source: File | string,
    requiresAnimation: boolean,
    requiresTransparency: boolean
): Promise<CreationToolSourceFile> =>
    new Promise((resolve, reject) => {
        const { isAnimated, isVideo, isUrl } = getSourceInfo(source)
        let fileType = isAnimated ? CreationToolFileType.ANIMATED_IMAGE : CreationToolFileType.STILL_IMAGE

        // images can't be uploaded as video, so reject
        if (isVideo) {
            reject(requiresAnimation ? CreationToolError.InvalidAnimatedFile : CreationToolError.InvalidStillFile)
            return
        }

        // if image is not animated, reject
        if (requiresAnimation && !isAnimated) {
            reject(CreationToolError.InvalidAnimatedFile)
            return
        }

        const img = document.createElement('img')
        const preview = document.createElement('canvas')

        img.onload = async () => {
            const isTransparent = checkIfSticker(img)
            const duration = await getImageDuration(img.src)

            if (requiresTransparency && !isTransparent) {
                reject(CreationToolError.NoTransparency)
            } else {
                preview.width = img.naturalWidth
                preview.height = img.naturalHeight
                preview.getContext('2d')?.drawImage(img, 0, 0)

                resolve({
                    id: uniqueId(),
                    source,
                    frames: [],
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                    type: fileType,
                    duration: duration / 100,
                    hasAudio: false,
                    sourceUrl: typeof source === 'string' ? source : undefined,
                    preview,
                    isTransparent,
                    isAnimated,
                    isUrl,
                })
            }

            if (typeof source !== 'string') {
                URL.revokeObjectURL(img.src)
            }
        }

        img.onerror = () => {
            reject(requiresAnimation ? CreationToolError.InvalidAnimatedFile : CreationToolError.InvalidStillFile)

            if (typeof source !== 'string') {
                URL.revokeObjectURL(img.src)
            }
        }

        img.crossOrigin = 'anonymous'
        img.src = typeof source === 'string' ? source : URL.createObjectURL(source)
    })

export default validateImage
